import React, { useState, useEffect, useMemo } from "react";
import { TreeSelect } from "antd";
import get from "lodash/get";
import debounce from "lodash/debounce";
import cx from "classnames";

import { api, queryBuilder } from "services";
import "./style.scss";

const buildTree = (items) => {
  const map = {};
  const roots = [];

  items.forEach(item => {
    map[item.id] = {
      title: item.name,
      value: item.id,
      parent_id: item.parent_id,
      children: [],
      selectable: false,
    };
  });

  items.forEach(item => {
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push({
        ...map[item.id],
        selectable: true,
      });
    }
  });

  items.forEach(item => {
    if (!item.parent_id) {
      if (!map[item.id].children.length) {
        map[item.id].selectable = true;
      }
      roots.push(map[item.id]);
    }
  });

  return roots;
};

const PAGE_SIZE = 15;

/** TreeSelect `value` must be the node id (treeData[].value), not the label. API may send { id, name }; tree onChange sends node props { value, title, ... }. */
const getTreeSelectId = (v) => {
  if (v == null || v === "") return undefined;
  if (typeof v === "object") {
    if (v.value !== undefined && v.value !== null) return v.value;
    if (v.id !== undefined && v.id !== null) return v.id;
  }
  return v;
};

const AsyncNestedSelectFormik = ({
  label,
  placeholder,
  isRequired,
  isDisabled,
  isClearable,
  isMulti = false,
  size = "default",
  loadOptionsUrl,
  loadOptionsParams = () => ({}),
  filterParams = {},
  field,
  form: { setFieldValue, setFieldTouched, errors, touched },
  className = "",
  canUpdate,
}) => {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allLoadedItems, setAllLoadedItems] = useState([]);

  useEffect(() => {
    if (canUpdate !== undefined) {
      loadData("", 1, false);
    }
  }, [canUpdate]);

  const hasError = touched[field.name] && errors[field.name];

  useEffect(() => {
    loadData("", 1, false);
  }, []);

  useEffect(() => {
    if (field.value && treeData.length === 0) {
      loadData("", 1);
    }
  }, [field.value]);

  const loadData = async (search = "", page = 1, isSearchMode = false) => {
    setLoading(true);

    const params = {
      ...filterParams,
      ...loadOptionsParams(search),
      per_page: PAGE_SIZE, // 15 tadan yuklash
      page: page,
    };

    if (!isSearchMode) {
      params.per_page = PAGE_SIZE; // Paginatsiya hajmi
      params.page = page;          // Sahifa raqami
    } else {
      // Qidiruv rejimida, API ruxsat bersa, katta per_page yuboramiz yoki paginatsiyasiz so'rov qilamiz
      // Sizning API-ingizda qidiruv bo'lsa, paginatsiyani o'chirish yoki katta miqdor so'rash afzal:
      // params.per_page = 1000; 
    }

    const res = await api.request.get(
      queryBuilder(loadOptionsUrl, params)
    );

    const data = get(res, "data", {});
    const items = data.data || [];
    const total = data.total;
    const lastPage = data.last_page;
    const currentPageFromResponse = data.current_page;

    let newLoadedItems;
    if (isSearchMode) {
      // Qidiruv natijasini alohida yuklash
      newLoadedItems = items;
      setAllLoadedItems(items); // Paginatsiya ro'yxatini tozalash
    } else if (page === 1) {
      // Birinchi sahifa
      newLoadedItems = items;
      setAllLoadedItems(items);
    } else {
      // Keyingi sahifalar uchun birlashtirish
      newLoadedItems = [...allLoadedItems, ...items];
      setAllLoadedItems(newLoadedItems);
    }

    setAllLoadedItems(newLoadedItems);

    setTreeData(
      isSearchMode
        ? newLoadedItems.map(i => ({
          title: i.name,
          value: i.id,
          selectable: true,
        }))
        : buildTree(newLoadedItems) // Barcha yuklangan itemlarni Tree quramiz
    );

    if (!isSearchMode) {
      setHasMore(currentPageFromResponse < lastPage);
      setCurrentPage(currentPageFromResponse);
    } else {
      // Qidiruvda paginatsiya tugadi deb hisoblashimiz mumkin
      setHasMore(false);
    }
    setLoading(false);
  };

  const fetchNextPage = () => {
    // Paginatsiya rejimida ekanligimizga ishonch hosil qilish
    if (!loading && hasMore && !searchValue) {
      // Keyingi sahifa raqami bilan yuklashni chaqirish
      loadData(searchValue, currentPage + 1, false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 10 && hasMore) {
      fetchNextPage();
    }
  };

  const debouncedLoadData = useMemo(
    () => debounce((search) => {
      if (search) {
        // Qidiruv bo'lganda, faqat qidiruvni yuklaymiz (isSearchMode = true)
        loadData(search, 1, true);
      } else {
        // Qidiruv bo'lmasa, 1-sahifani yuklaymiz (Normal Paginatsiya)
        loadData("", 1, false);
      }
    }, 400),
    []
  );

  useEffect(() => {
    return () => debouncedLoadData.cancel();
  }, []);

  return (
    <div
      className={cx(
        "form-field",
        className,
        hasError && "form-field--error",
        getTreeSelectId(field.value) != null && "form-field--active"
      )}
    >
      {label && (
        <label className="form-field__label">
          {label}
          {isRequired && <span>*</span>}
        </label>
      )}

      <>
        <TreeSelect
          value={getTreeSelectId(field.value)}
          treeData={treeData}
          treeDefaultExpandAll
          labelInValue={false}
          showSearch
          filterTreeNode={false}
          placeholder={placeholder}
          allowClear={isClearable}
          disabled={isDisabled}
          multiple={isMulti}
          loading={loading}
          className="custom-treeselect"
          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
          dropdownRender={menu => (
            <div onScroll={handleScroll}>
              {menu}
              {loading && (
                <div style={{ textAlign: 'center', padding: '5px' }}>
                  Yuklanmoqda...
                </div>
              )}
              {!hasMore && treeData.length > 0 && (
                <div style={{ textAlign: 'center', padding: '5px', color: '#999' }}>
                  Barcha ma'lumotlar yuklandi.
                </div>
              )}
            </div>
          )}
          maxTagCount="responsive"
          getPopupContainer={node => node.parentNode}
          onBlur={() => setFieldTouched(field.name, true)}
          onSearch={val => {
            setSearchValue(val);
            // Debounce orqali qidiruvni chaqirish
            debouncedLoadData(val);
          }}
          onDropdownVisibleChange={open => {
            if (!open && searchValue) {
              // Dropdown yopilganda va qidiruv bo'lgan bo'lsa
              setSearchValue("");
              // Boshlang'ich (paginatsiyalangan) ro'yxatga qaytish
              loadData("", 1, false);
            }
          }}
          onChange={(val, _, extra) => {
            setFieldValue(field.name, get(extra, "triggerNode.props"));

            // agar to‘liq node kerak bo‘lsa
            // const node =
            //   isMulti
            //     ? get(extra, "allCheckedNodes", []).map(n => n.node.props)
            //     : get(extra, "triggerNode.props");

            // handleChange?.(val, node);
          }}
        />

        {hasError && (
          <div className="form-field__error top-[15px]">
            <span>{errors[field.name]}</span>
          </div>
        )}
      </>
    </div>
  );
};

export default AsyncNestedSelectFormik;
